import { useState, useEffect } from "react";
import { fetchData } from "../../api";

export default function Reviews() {
    const [reviews, setReviews] = useState([]);
    const [currentReview, setCurrentReview] = useState(0);
    const [reviewStats, setReviewStats] = useState(null);

    useEffect(() => {
        fetchData("client-reviews", true).then(data => setReviews(data || []));
        fetchData("review-stats", true).then(data => setReviewStats(data));
    }, []);

    useEffect(() => {
        if (reviews.length > 1) {
            const interval = setInterval(() => {
                setCurrentReview((prev) => (prev + 1) % reviews.length);
            }, 12000);
            return () => clearInterval(interval);
        }
    }, [reviews.length]);

    if (!reviews.length) return null;

    const currentReviewData = reviews[currentReview];

    // Function to extract text from Strapi blocks
    const extractTextFromBlocks = (blocks) => {
        if (!blocks) return "";
        if (typeof blocks === "string") return blocks;
        
        if (Array.isArray(blocks)) {
            return blocks.map(block => {
                if (block.type === "paragraph" && block.children) {
                    return block.children.map(child => child.text || "").join("");
                }
                return "";
            }).join(" ");
        }
        
        if (blocks.children && Array.isArray(blocks.children)) {
            return blocks.children.map(child => child.text || "").join("");
        }
        
        return "";
    };

    const reviewText = extractTextFromBlocks(currentReviewData.Review);

    return (
        <section className="relative py-32 bg-gradient-to-br from-slate-50 via-white to-slate-100 overflow-hidden">
            {/* Subtle Background Pattern */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(40,103,102,0.03)_1px,transparent_0)] bg-[length:40px_40px]"></div>
            
            {/* Floating Elements */}
            <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-[#286766]/5 to-transparent rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-tl from-[#27352C]/5 to-transparent rounded-full blur-3xl"></div>

            <div className="container mx-auto px-4 relative z-10">
                {/* Header */}
                <div className="text-center mb-20">
                    <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-[#286766]/20 shadow-sm">
                        <div className="w-2 h-2 bg-[#286766] rounded-full animate-pulse"></div>
                        <span className="text-sm font-medium text-[#286766]">Klantenbeoordelingen</span>
                    </div>
                    <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
                        Wat onze klanten{" "}
                        <span className="bg-gradient-to-r from-[#286766] to-[#27352C] bg-clip-text text-transparent">
                            zeggen
                        </span>
                    </h2>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                        Echte ervaringen van tevreden klanten die ons vertrouwd hebben met hun projecten
                    </p>
                </div>

                {/* Main Review Display */}
                <div className="max-w-5xl mx-auto">
                    <div className="relative min-h-[600px] sm:min-h-[500px]">
                        {/* Review Card */}
                        <div className="relative bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden transform transition-all duration-1000 hover:scale-[1.02] hover:shadow-2xl">
                            {/* Gradient Accent */}
                            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#286766] via-[#27352C] to-[#286766]"></div>
                            
                            <div className="p-8 sm:p-12 md:p-16">
                                {/* Quote Icon */}
                                <div className="mb-6 sm:mb-8">
                                    <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-[#286766] to-[#27352C] rounded-2xl shadow-lg">
                                        <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
                                        </svg>
                                    </div>
                                </div>

                                {/* Review Text */}
                                <div className="mb-8 sm:mb-10">
                                    <p className="text-lg sm:text-xl md:text-2xl text-gray-800 leading-relaxed font-light">
                                        "{reviewText}"
                                    </p>
                                </div>

                                {/* Client Info */}
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <div className="flex items-center gap-4">
                                        {/* Avatar Placeholder */}
                                        <div className="w-16 h-16 bg-gradient-to-br from-[#286766] to-[#27352C] rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                                            <span className="text-2xl font-bold text-white">
                                                {currentReviewData.Name?.charAt(0) || "C"}
                                            </span>
                                        </div>
                                        
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                <h3 className="text-xl font-bold text-gray-900 truncate">
                                                    {currentReviewData.Name}
                                                </h3>
                                                {/* Google Icon */}
                                                <svg className="w-5 h-5 text-[#4285F4] flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                                </svg>
                                            </div>
                                            <p className="text-gray-600">Google Review</p>
                                        </div>
                                    </div>

                                    {/* Rating */}
                                    <div className="flex items-center gap-3 flex-shrink-0">
                                        <div className="flex gap-1">
                                            {[...Array(5)].map((_, i) => (
                                                <svg
                                                    key={i}
                                                    className={`w-5 h-5 sm:w-6 sm:h-6 ${
                                                        i < Math.floor(currentReviewData.Rating || 0)
                                                            ? "text-yellow-400"
                                                            : "text-gray-300"
                                                    }`}
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                >
                                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                                </svg>
                                            ))}
                                        </div>
                                        <span className="text-base sm:text-lg font-semibold text-gray-900">
                                            {currentReviewData.Rating}/5
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Navigation */}
                        {reviews.length > 1 && (
                            <div className="flex justify-center items-center gap-6 mt-12">
                                <button
                                    onClick={() => setCurrentReview((prev) => (prev - 1 + reviews.length) % reviews.length)}
                                    className="group w-14 h-14 bg-white rounded-2xl shadow-lg border border-gray-200 flex items-center justify-center hover:shadow-xl transition-all duration-300 hover:scale-110"
                                >
                                    <svg className="w-6 h-6 text-gray-600 group-hover:text-[#286766] transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                </button>
                                
                                <div className="flex gap-3">
                                    {reviews.map((_, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setCurrentReview(index)}
                                            className={`w-3 h-3 rounded-full transition-all duration-500 ${
                                                index === currentReview
                                                    ? "bg-[#286766] scale-125 shadow-lg"
                                                    : "bg-gray-300 hover:bg-gray-400"
                                            }`}
                                        />
                                    ))}
                                </div>
                                
                                <button
                                    onClick={() => setCurrentReview((prev) => (prev + 1) % reviews.length)}
                                    className="group w-14 h-14 bg-white rounded-2xl shadow-lg border border-gray-200 flex items-center justify-center hover:shadow-xl transition-all duration-300 hover:scale-110"
                                >
                                    <svg className="w-6 h-6 text-gray-600 group-hover:text-[#286766] transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Bottom Stats */}
                    <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="text-center">
                            <div className="text-3xl font-bold text-[#286766] mb-2">
                                {reviewStats?.attributes?.SatisfactionPercentage || "100%"}
                            </div>
                            <div className="text-gray-600">
                                {reviewStats?.attributes?.SatisfactionLabel || "Tevredenheid"}
                            </div>
                        </div>
                        <div className="text-center">
                            <div className="text-3xl font-bold text-[#286766] mb-2">
                                {reviewStats?.attributes?.ProjectsCount || "500+"}
                            </div>
                            <div className="text-gray-600">
                                {reviewStats?.attributes?.ProjectsLabel || "Projecten Voltooid"}
                            </div>
                        </div>
                        <div className="text-center">
                            <div className="text-3xl font-bold text-[#286766] mb-2">
                                {reviewStats?.attributes?.AverageRating || "5.0"}
                            </div>
                            <div className="text-gray-600">
                                {reviewStats?.attributes?.AverageRatingLabel || "Gemiddelde Beoordeling"}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
